/* eslint-disable */
import { Injectable } from '@angular/core';
import { SQLite } from '@ionic-native/sqlite/ngx';
import { Platform } from '@ionic/angular';
import { MSRemoteLoggerCategory, MSRemoteLoggerSeverity } from '../adapters/remote-logger/utils/remote-logger.utils';
import { DebugLoggingCapsule } from '../use-case/feature-flags/capsule/debug-logging.capsule';
import { RemoteLoggerUseCase } from '../use-case/remote-logger/remote-logger.use-case';
import { ApiServiceOld } from './api-old.service';
import { CONSTANTS } from './config.service';
import { LoggerService } from './logger.service';

const win: any = window;
const execPath = '/execsql';

// TODO detect browserMove (build config setting?) and treat that differently than !IS_DEVICE (still useful for testing locally, including auto-tester)

@Injectable({
  providedIn: 'root'
})
export class SqlHelperService {
  COMP_BETWEEN = '_BETWEEN';
  COMP_IN = '_IN';
  COMP_GT = '_GT';
  COMP_GE = '_GE';
  COMP_LT = '_LT';
  COMP_LE = '_LE';
  COMP_NE = '_NE';
  LITERAL = '_LITERAL';
  NULL = '_NULL';
  NOT_NULL = '_NOTNULL';
  // for multiple OR/NOT keys in the passed in whereObj, follow w/ digits
  OR = /^OR[0-9]*/;
  NOT = /^NOT[0-9]*$/;

  authCode = null;

  constructor(
    private sqlite: SQLite,
    private logger: LoggerService,
    private platform: Platform,
    private api: ApiServiceOld,
    private remoteLogger: RemoteLoggerUseCase,
    private debugLoggingCapsule: DebugLoggingCapsule
  ) {}

  private concat(arr, strOrArr) {
    if (Array.isArray(strOrArr)) {
      strOrArr.forEach(function (value) {
        // not recursive/nested
        arr.push(value);
      });
    } else {
      arr.push(strOrArr);
    }
  }

  private getCommaedQMarks(count) {
    return new Array(count).join('?, ') + '?';
  }

  private getWhere(whereObject, isOr = false) {
    const self = this;
    const whereObj = whereObject || {};
    const cols: Array<string> = Object.keys(whereObj || {});
    let template: Array<string> = [];
    let parameters: Array<any> = [];

    cols.forEach((col) => {
      if (col.indexOf('_') > -1 && !col.startsWith('_CB_')) {
        // HACK_CB - TODO use _FIELD instead and build this
        let paramsHandled = false;
        if (col.endsWith(self.COMP_IN)) {
          template.push([col.replace(self.COMP_IN, ' IN ('), self.getCommaedQMarks(whereObj[col].length), ')'].join(''));
          parameters = parameters.concat(whereObj[col]);
          paramsHandled = true;
        } else if (col.endsWith(self.COMP_BETWEEN)) {
          template.push(col.replace(self.COMP_BETWEEN, ' BETWEEN ') + '? AND ?');
          parameters = parameters.concat(whereObj[col]);
          paramsHandled = true;
        } else if (col.endsWith(self.COMP_GT)) {
          template.push(col.replace(self.COMP_GT, '>?'));
        } else if (col.endsWith(self.COMP_GE)) {
          template.push(col.replace(self.COMP_GE, '>=?'));
        } else if (col.endsWith(self.COMP_LT)) {
          template.push(col.replace(self.COMP_LT, '<?'));
        } else if (col.endsWith(self.COMP_LE)) {
          template.push(col.replace(self.COMP_LE, '<=?'));
        } else if (col.endsWith(self.COMP_NE)) {
          template.push(col.replace(self.COMP_NE, '!=?'));
        } else if (col.endsWith(self.LITERAL)) {
          template.push(col.replace(self.LITERAL, '=' + whereObj[col]));
          paramsHandled = true;
        } else if (col.endsWith(self.NULL)) {
          template.push(col.replace(self.NULL, ' IS NULL'));
          paramsHandled = true;
        } else if (col.endsWith(self.NOT_NULL)) {
          template.push(col.replace(self.NOT_NULL, ' IS NOT NULL'));
          paramsHandled = true;
        }

        if (!paramsHandled) {
          parameters.push(whereObj[col]);
        }
      } else if (col.match(self.OR)) {
        const subWhere = self.getWhere(whereObj[col], true);
        template.push('(' + subWhere.conditions + ')');
        parameters = parameters.concat(subWhere.parameters);
      } else if (col.match(self.NOT)) {
        const subWhere = self.getWhere(whereObj[col]);
        parameters = parameters.concat(subWhere.parameters);
        template.push('NOT (' + subWhere.conditions + ')');
      } else {
        template.push(col + '=?');
        parameters.push(whereObj[col]);
      }
    });

    if (!template.length) {
      template = ['1=1'];
    }
    return { conditions: template.join(isOr ? ' OR ' : ' AND '), parameters };
  }

  private removeSuffix(str, suffix) {
    return str.substring(0, str.length - suffix.length);
  }

  private getSetClause(valueObject) {
    const self = this;
    const cols = Object.keys(valueObject);
    const assignments = [];
    const params = [];

    cols.forEach(function (col) {
      let value = valueObject[col];
      value = value === true ? 1 : value === false ? 0 : value;
      if (col.endsWith(self.LITERAL)) {
        assignments.push([self.removeSuffix(col, self.LITERAL), '=', value].join(''));
      } else {
        assignments.push(col + '=?');
        params.push(value);
      }
    });
    return { assignments: assignments.join(', '), parameters: params };
  }

  private getSelectClause(cols) {
    const self = this;
    return cols
      .map(function (col) {
        if (!col) {
          throw 'Bad column "' + col + '" in "' + cols + '"';
        }
        if (col.endsWith(self.LITERAL)) {
          return self.removeSuffix(col, self.LITERAL);
        } else {
          return col;
        }
      })
      .join(', ');
  }

  private getArray(sqlResultSetList) {
    const out = [];
    const len = sqlResultSetList.length;
    let i;
    for (i = 0; i < len; i++) {
      out.push(sqlResultSetList.item(i));
    }
    //this.logger.log('getArray', out);
    return out;
  }

  private execute(db, sql, params) {
    this.remoteLogger.addBreadcrumbs(
      `execute: ${db} ${sql}`,
      MSRemoteLoggerCategory.SQL_HELPER_SERVICE,
      MSRemoteLoggerSeverity.INFO,
      params
    );

    const self = this;
    const getResult = function (results) {
      //self.logger.log('getResult', results);
      if (!results) {
        //self.logger.log('No results', results, 'for', sql, 'with', params);
        return results;
      }

      if (sql.startsWith('SELECT')) {
        if (results.rows) {
          return self.getArray(results.rows);
        } else {
          // server db e.g. {"data": [[234, "aasdf" ]], "headers": ["i", "t"]}
          const out = [];
          results.data.forEach((row) => {
            const obj = {};
            results.headers.forEach((col, idx) => {
              obj[col] = row[idx];
            });
            out.push(obj);
          });
          return out;
        }
      } else if (sql.startsWith('INSERT')) {
        return results.insertId;
      } else {
        return results.rowsAffected;
      }
    };
    if (CONSTANTS.USE_SERVER_DB) {
      return new Promise((resolve, reject) => {
        if (!this.authCode) {
          this.remoteLogger.addBreadcrumbs('execute - no auth', MSRemoteLoggerCategory.SQL_HELPER_SERVICE, MSRemoteLoggerSeverity.WARN);
          reject('No authCode for sql helper ' + sql + ', ' + params);
        } else {
          //let encSql = CryptoJS.RC4.encrypt(sql, "l$kD@Sdf-3li=f93;)djw").toString();

          this.api.post(execPath, { authCode: this.authCode, sql, params: JSON.stringify(params) }).subscribe(
            (results) => {
              //this.logger.log("Remote results", results);
              resolve(getResult(results));
            },
            (err) => {
              this.remoteLogger.captureException(err);
              this.logger.error(err, MSRemoteLoggerCategory.SQL_HELPER_SERVICE);

              reject(err);
            }
          );
        }
      });
    } else if (CONSTANTS.IS_DEVICE) {
      return new Promise((resolve, reject) => {
        db.executeSql(
          sql,
          params,
          (res) => {
            resolve(getResult(res));
          },
          (err) => {
            this.remoteLogger.captureException(err);
            this.logger.error(err, MSRemoteLoggerCategory.SQL_HELPER_SERVICE);

            reject(err);
          }
        );
      });
    } else {
      // for local db, like auto-testing and survey development
      // db.executeSql api not supported by browser
      // not using this for phones - uncommitted transaction bug? (finished surveys look unfinished on roster)
      return new Promise(function (resolve, reject) {
        let results;
        db.transaction(
          function (tx) {
            tx.executeSql(
              sql,
              params,
              function (innerTx, res) {
                results = res;
              },
              function (innerTx, err) {
                this.remoteLogger.captureException(err);
                self.logger.error('Failed', MSRemoteLoggerCategory.SQL_HELPER_SERVICE, innerTx, err);
                reject(err);
              }
            );
          },
          function (err) {
            this.remoteLogger.captureException(err);
            self.logger.error('tx failed', MSRemoteLoggerCategory.SQL_HELPER_SERVICE, err);
            reject(err);
          },
          function () {
            //self.logger.info('tx success');
            resolve(getResult(results));
          }
        );
      });
    }
  }

  getSelectPromise(db, tableName, cols, whereObj?, orderBy?, limit?, groupBy?): Promise<any[]> {
    const where = this.getWhere(whereObj, null);
    const selectStatement = ['SELECT ', this.getSelectClause(cols), ' FROM ', tableName, ' WHERE ', where.conditions];
    if (orderBy) {
      this.concat(selectStatement, [' ORDER BY ', orderBy]);
    }
    if (limit) {
      this.concat(selectStatement, [' LIMIT ', limit]);
    }

    const selectString = selectStatement.join('');
    const parameters = where.parameters;

    return this.execute(db, selectString, parameters) as Promise<any[]>;
  }

  getInsertPromise(db, tableName, valueObject) {
    this.debugLoggingCapsule.logIfAble(`insert: ${db?.dbName}: ${JSON.stringify(tableName)}: value: ${JSON.stringify(valueObject)}`);

    const cols = Object.keys(valueObject);
    const params = cols.map(function (col) {
      return valueObject[col];
    });
    const insertStatement = ['INSERT INTO ', tableName, ' (', cols.join(','), ') VALUES (', this.getCommaedQMarks(cols.length), ');'].join(
      ''
    );
    return this.execute(db, insertStatement, params);
  }

  getUpdatePromise(db, tableName, valueObject, whereObj) {
    this.debugLoggingCapsule.logIfAble(`update: ${db?.dbName}; ${JSON.stringify(tableName)}; value: ${JSON.stringify(valueObject)}; where: ${JSON.stringify(whereObj)}`);

    const where = this.getWhere(whereObj, null);
    const setClause = this.getSetClause(valueObject);
    const updateStatement = ['UPDATE ', tableName, ' SET ', setClause.assignments, ' WHERE ', where.conditions, ';'].join('');
    const allParams = setClause.parameters.concat(where.parameters);
    return this.execute(db, updateStatement, allParams);
  }

  getDeletePromise(db, tableName, whereObj) {
    this.debugLoggingCapsule.logIfAble(`delete: ${db?.dbName}; table: ${JSON.stringify(tableName)}; where: ${JSON.stringify(whereObj)}`);

    const where = this.getWhere(whereObj, null);
    const deleteStatement = ['DELETE FROM ', tableName, ' WHERE ', where.conditions, ';'].join('');
    return this.execute(db, deleteStatement, where.parameters);
  }

  getCountPromise(db, tableName, whereObj, countExpression?) {
    countExpression = countExpression || '*';
    return this.getSelectPromise(db, tableName, ['count(' + countExpression + ') AS countAlias'], whereObj).then(
      (rows) => rows[0].countAlias
    );
  }

  getExistsPromise(db, tableName, whereObj?) {
    return this.getSelectPromise(db, tableName, ['1'], whereObj, null, 1, null).then((rows: any[]) => rows.length === 1);
  }

  createTablePromise(db, sql) {
    this.debugLoggingCapsule.logIfAble(`createTable: ${db?.dbName}; sql: ${JSON.stringify(sql)}`);

    return this.execute(db, sql, []);
  }

  createDb(params) {
    this.debugLoggingCapsule.logIfAble(`createDb: ${params?.name}`);

    if (CONSTANTS.USE_SERVER_DB) {
      // no-op for browsermove - db is autocreated on server with first access (named with authCode, so only 1 per app)
      return Promise.resolve(null); // don't need db, server manages
    } else if (CONSTANTS.IS_DEVICE) {
      return this.sqlite.create(params);
      // androidDatabaseProvider: 'system'
    } else {
      // use win.openDatabase for ionic serve, only works on some browsers
      return Promise.resolve(win.openDatabase(params.name, '1.0', 'database', 5 * 1024 * 1024));
    }
  }

  getSecondsColAsYmd(col, colAlias) {
    colAlias = colAlias || 'ymd';
    return "strftime('%Y%m%d', " + col + ", 'unixepoch', 'localtime') AS " + colAlias + '_LITERAL';
  }

  initialize(authCode): Promise<any> {
    this.authCode = authCode; // so prefs can use sqlHelper and sqlHelper doesn't need prefs
    return Promise.resolve();
  }
}
